/**
 * Cost Variations Management Page
 */
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';
import { toMinor, toMajor } from '../../lib/currency';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Switch } from '../../components/ui/switch';
import { Modal } from '../../components/ui/modal';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { HelpAlert, HelpTooltip, HelpGuide } from '../../components/ui/HelpGuide';
import { 
  Settings,
  Plus,
  Edit,
  Trash2,
  History,
  DollarSign,
  Percent,
  Package,
  Tag,
  RefreshCw,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

export default function CostVariationsPage() {
  const { formatCurrency } = useInstance();
  
  const [loading, setLoading] = useState(true);
  const [variations, setVariations] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [showModal, setShowModal] = useState(false);
  const [editingVariation, setEditingVariation] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    description: '',
    code: '',
    type: 'fixed',
    value: 0,
    scope: 'all',
    category_ids: [],
    is_active: true
  });
  
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [varRes, catRes] = await Promise.all([
        api.get('/cost-variations'),
        api.get('/categories')
      ]);
      
      setVariations(varRes.data?.items || []);
      setCategories(catRes.data?.items || catRes.data || []);
    } catch (err) {
      toast.error('Erro ao carregar variações');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (variation = null) => {
    if (variation) {
      setEditingVariation(variation);
      setForm({
        name: variation.name,
        description: variation.description || '',
        code: variation.code || '',
        type: variation.type,
        value: variation.value,
        scope: variation.scope,
        category_ids: variation.category_ids || [],
        is_active: variation.is_active
      });
    } else {
      setEditingVariation(null);
      setForm({
        name: '',
        description: '',
        code: '',
        type: 'fixed',
        value: 0,
        scope: 'all',
        category_ids: [],
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name) {
      toast.error('Nome é obrigatório');
      return;
    }
    
    try {
      setSaving(true);
      
      // Convert value to minor units for fixed type
      const dataToSave = {
        ...form,
        value_minor: form.type === 'fixed' ? toMinor(form.value) : form.value * 100, // percentage stored as int (30% = 3000)
      };
      delete dataToSave.value;
      
      if (editingVariation) {
        await api.put(`/cost-variations/${editingVariation.id}`, dataToSave);
        toast.success('Variação atualizada!');
      } else {
        await api.post('/cost-variations', dataToSave);
        toast.success('Variação criada!');
      }
      
      setShowModal(false);
      loadData();
    } catch (err) {
      toast.error('Erro ao salvar variação');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta variação?')) return;
    
    try {
      await api.delete(`/cost-variations/${id}`);
      toast.success('Variação excluída!');
      loadData();
    } catch (err) {
      toast.error('Erro ao excluir');
    }
  };

  const handleViewHistory = (variation) => {
    setSelectedVariation(variation);
    setShowHistoryModal(true);
  };

  const handleCategoryToggle = (categoryId) => {
    setForm(prev => ({
      ...prev,
      category_ids: prev.category_ids.includes(categoryId)
        ? prev.category_ids.filter(id => id !== categoryId)
        : [...prev.category_ids, categoryId]
    }));
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">Carregando variações...</div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="cost-variations-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/pricing">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="h-6 w-6" />
              Variações de Custo
            </h1>
            <p className="text-muted-foreground">
              Configure custos adicionais que impactam o preço final
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={() => loadData()} variant="ghost" size="icon">
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => handleOpenModal()} data-testid="new-variation-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nova Variação
          </Button>
        </div>
      </div>

      {/* Help */}
      <HelpGuide
        title="O que são Variações de Custo?"
        collapsible={true}
        defaultOpen={false}
        className="mb-6"
        description="Variações de custo são custos adicionais que devem ser considerados no preço final do produto."
        tips={[
          "Embalagem: custo da embalagem por unidade vendida",
          "Gás: custo do gás rateado por produto (use a calculadora de proporção)",
          "Impostos: percentual de imposto sobre o preço",
          "Frete: custo de frete por unidade"
        ]}
      />

      {/* Variations List */}
      {variations.length === 0 ? (
        <HelpAlert variant="info" title="Nenhuma variação cadastrada">
          Crie variações de custo como embalagem, gás, impostos para incluir no cálculo de preços.
        </HelpAlert>
      ) : (
        <div className="grid gap-4">
          {variations.map((variation) => (
            <Card key={variation.id} className={!variation.is_active ? 'opacity-50' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    {variation.type === 'fixed' ? (
                      <DollarSign className="h-6 w-6 text-primary" />
                    ) : (
                      <Percent className="h-6 w-6 text-primary" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{variation.name}</h3>
                      {variation.code && (
                        <Badge variant="outline">{variation.code}</Badge>
                      )}
                      {!variation.is_active && (
                        <Badge variant="secondary">Inativo</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {variation.description || 'Sem descrição'}
                    </p>
                  </div>
                  
                  <div className="text-center px-4">
                    <div className="text-sm text-muted-foreground">Valor</div>
                    <div className="text-xl font-bold">
                      {variation.type === 'fixed' 
                        ? formatCurrency(variation.value)
                        : `${variation.value}%`
                      }
                    </div>
                  </div>
                  
                  <div className="text-center px-4">
                    <div className="text-sm text-muted-foreground">Escopo</div>
                    <Badge variant="outline" className="flex items-center gap-1">
                      {variation.scope === 'all' && <Package className="h-3 w-3" />}
                      {variation.scope === 'category' && <Tag className="h-3 w-3" />}
                      {variation.scope === 'all' && 'Todos'}
                      {variation.scope === 'category' && `${variation.category_ids?.length || 0} categorias`}
                      {variation.scope === 'product' && `${variation.product_ids?.length || 0} produtos`}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleViewHistory(variation)}
                      title="Ver histórico"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleOpenModal(variation)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDelete(variation.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        open={showModal}
        onOpenChange={setShowModal}
        title={editingVariation ? 'Editar Variação' : 'Nova Variação de Custo'}
        confirmText={editingVariation ? 'Salvar' : 'Criar'}
        loading={saving}
        onConfirm={handleSave}
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Embalagem"
              />
            </div>
            <div className="space-y-2">
              <Label>Código</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm(prev => ({ ...prev, code: e.target.value }))}
                placeholder="Ex: EMB"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descrição da variação..."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo</Label>
              <Select 
                value={form.type} 
                onValueChange={(v) => setForm(prev => ({ ...prev, type: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">
                    <span className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4" /> Valor Fixo (R$)
                    </span>
                  </SelectItem>
                  <SelectItem value="percentage">
                    <span className="flex items-center gap-2">
                      <Percent className="h-4 w-4" /> Percentual (%)
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Valor</Label>
              <Input
                type="number"
                value={form.value}
                onChange={(e) => setForm(prev => ({ ...prev, value: Number(e.target.value) }))}
                min={0}
                step={form.type === 'fixed' ? 0.01 : 0.1}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Aplicar em</Label>
            <Select 
              value={form.scope} 
              onValueChange={(v) => setForm(prev => ({ ...prev, scope: v }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os Produtos</SelectItem>
                <SelectItem value="category">Categorias Específicas</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.scope === 'category' && (
            <div className="space-y-2">
              <Label>Categorias</Label>
              <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border rounded-lg">
                {categories.map(cat => (
                  <div key={cat.id} className="flex items-center gap-2">
                    <Checkbox
                      checked={form.category_ids.includes(cat.id)}
                      onCheckedChange={() => handleCategoryToggle(cat.id)}
                    />
                    <Label className="text-sm">{cat.name}</Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            <Label>Ativo</Label>
            <Switch
              checked={form.is_active}
              onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))}
            />
          </div>
        </div>
      </Modal>

      {/* History Modal */}
      <Modal
        open={showHistoryModal}
        onOpenChange={setShowHistoryModal}
        title={`Histórico: ${selectedVariation?.name}`}
        showConfirm={false}
        cancelText="Fechar"
      >
        {selectedVariation?.history && selectedVariation.history.length > 0 ? (
          <div className="divide-y max-h-64 overflow-y-auto">
            {selectedVariation.history.map((entry, idx) => (
              <div key={idx} className="flex items-center justify-between py-2">
                <div className="text-sm">
                  {new Date(entry.effective_date).toLocaleDateString('pt-BR')}
                </div>
                <Badge variant="outline">
                  {selectedVariation.type === 'fixed' 
                    ? formatCurrency(entry.value)
                    : `${entry.value}%`
                  }
                </Badge>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Nenhum histórico disponível</p>
        )}
      </Modal>
    </div>
  );
}
